import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { usePhase } from "../context/PhaseContext";
import { LOGO_URL } from "../data/content";

const links = [
  { label: "Chapter I", href: "/chapter-i" },
  { label: "Archive", href: "/archive" },
  { label: "Society", href: "/society" },
  { label: "About", href: "/about" },
];

export const Navbar = ({ onJoin }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { phase } = usePhase();
  const commerce = phase >= 5;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleJoin = () => {
    if (pathname === "/" && onJoin) onJoin();
    else navigate("/#join");
  };

  const navItems = commerce ? [{ label: "Shop", href: "/#shop" }, ...links] : links;

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: pathname === "/" ? 2.4 : 0.2, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        open ? "bg-bds-bg" : scrolled ? "backdrop-blur-xl bg-bds-bg/70 border-b border-bds-border" : ""
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="/" data-testid="brand-logo" className="flex items-center group" aria-label="Black Dog Society — home">
          <img
            src={LOGO_URL}
            alt="Black Dog Society"
            className="h-11 md:h-14 w-auto object-contain mix-blend-screen select-none"
            draggable={false}
          />
        </a>

        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {navItems.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono-bds text-xs uppercase tracking-[0.2em] transition-colors ${
                pathname === l.href ? "text-bds-fg" : "text-bds-muted hover:text-bds-fg"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={handleJoin}
            data-testid="nav-join-btn"
            className="font-mono-bds text-xs uppercase tracking-[0.2em] px-6 py-2.5 border border-bds-fg hover:bg-bds-fg hover:text-bds-bg transition-colors duration-300"
          >
            Enter the Society
          </button>
          {commerce && (
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart-btn"
              className="relative w-11 h-11 grid place-items-center border border-bds-border hover:border-bds-fg transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={16} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 grid place-items-center bg-bds-accent text-bds-cream font-mono-bds text-[10px] rounded-full">
                  {count}
                </span>
              )}
            </button>
          )}
        </nav>

        <div className="md:hidden flex items-center gap-3">
          {commerce && (
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart-btn-mobile"
              className="relative w-10 h-10 grid place-items-center border border-bds-border"
              aria-label="Open cart"
            >
              <ShoppingBag size={15} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 grid place-items-center bg-bds-accent text-bds-cream font-mono-bds text-[9px] rounded-full">
                  {count}
                </span>
              )}
            </button>
          )}
          <button
            className="font-mono-bds text-xs uppercase tracking-[0.2em] min-h-[44px] px-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            aria-label="Mobile"
            className="md:hidden overflow-hidden bg-bds-bg border-b border-bds-border"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navItems.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif-display text-3xl"
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); handleJoin(); }}
                className="mt-2 self-start font-mono-bds text-xs uppercase tracking-[0.2em] px-6 py-3 border border-bds-fg"
              >
                Enter the Society
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
